// Inteligencia › Acopio: inventario aproximado de lo acopiado en la obra según la cámara (pallets, bolsones,
// pilas de arena y piedra, hierros, maderas, caños): cuánto hay hoy, cómo cambió día a día, consumo estimado
// y avisos del tipo "ayer había 6 pallets y hoy 2" con la foto de antes y la de ahora.
//
// Cómo se cuenta (en el navegador, sobre las detecciones iaV3): en cada día se juntan las cajas de la misma clase
// que están en el mismo lugar en distintas fotos (= un objeto acopiado). Cuenta solo lo que se ve en al menos el
// 30 % de las fotos del día: así no suma lo que pasó un rato (una carretilla, un camión) ni se pierde lo que una
// persona o un camión tapó un momento. Las pilas (arena, piedra) se miden además por la superficie que ocupan en la foto.
import React, { useMemo, useState } from 'react';
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid } from 'recharts';
import { T, Btn, Card, Chip } from '../ui';
import { fmtHM, fmtKey, fmtDow } from '../iaData';
import { Photo, Visor } from '../CalendarioObra';
import { MAT, recorte, plural, pct } from './datosV3';
import { calcular, DIA_COMPLETO } from './acopioData';
import { useFotosV3, useNarrow, Encabezado, Rango, Nota, Cargando, Vacio, SinAnalisis, Kpi, textoRango } from './comun';
import { nuevoPdf, imagenRecorte, nombreArchivo } from './pdf';

const cap = (s) => s.charAt(0).toUpperCase() + s.slice(1);
const num = (x, d = 1) => String(Math.round(x * 10 ** d) / 10 ** d).replace('.', ',');

const cajaUnion = (p, cajas) => {
  if (!cajas.length) return null;
  const u = { x1: Math.min(...cajas.map((b) => b.x1)), y1: Math.min(...cajas.map((b) => b.y1)), x2: Math.max(...cajas.map((b) => b.x2)), y2: Math.max(...cajas.map((b) => b.y2)) };
  return recorte(p, u, { k: 1.6, ratio: 16 / 9, min: 0.22 });
};

// Misma zona para la foto de antes y la de ahora: alrededor de todo lo que se vio en los dos días
const zonaAviso = (av) => cajaUnion(av.b.foto || av.a.foto, av.a.cajas.concat(av.b.cajas));

function Aviso({ av, onVer, narrow }) {
  const m = MAT[av.c];
  const zona = zonaAviso(av);
  const fotoDe = (x, label) => (x.foto ? (
    <div style={{ minWidth: 0 }}>
      <Photo photo={x.foto} crop={zona} boxes={x.cajas.map((d) => ({ ...d, color: m.color }))}
        onClick={() => onVer(av)} style={{ cursor: 'zoom-in', borderRadius: 8 }} />
      <div style={{ fontSize: 11, color: T.muted, marginTop: 3 }}>{label} · {fmtKey(x.dia)} {fmtHM(x.foto.ms)}</div>
    </div>
  ) : null);
  return (
    <div style={{ border: `1px solid ${av.baja ? '#fde68a' : '#a7f3d0'}`, borderLeft: `4px solid ${av.baja ? '#f59e0b' : '#10b981'}`, borderRadius: 12, padding: 12 }}>
      <div style={{ fontWeight: 700, fontSize: 14 }}>{m.icon} {av.texto}</div>
      <div style={{ display: 'grid', gridTemplateColumns: `repeat(2, minmax(0, 1fr))`, gap: 8, marginTop: 8, maxWidth: narrow ? '100%' : 620 }}>
        {fotoDe(av.a, 'Antes')}
        {fotoDe(av.b, 'Ahora')}
      </div>
    </div>
  );
}

async function generarPdf({ name, rango, res }) {
  const ult = res.dias[res.iHoy];
  const pdf = nuevoPdf({
    titulo: 'Acopio en obra', obra: name, periodo: textoRango(rango),
    nota: 'Conteo aproximado de lo que se ve desde la cámara: no incluye lo tapado, lo guardado bajo techo ni lo que queda fuera de cuadro. Las pilas se miden por la superficie que ocupan en la foto. Generado automáticamente a partir de las fotos de la cámara.',
  });
  pdf.titulo(`Inventario al ${fmtKey(ult)}`);
  pdf.tabla([{ t: 'Material', w: 70 }, { t: 'Hoy', w: 34 }, { t: 'Primer día completo', w: 44 }, { t: 'Consumo estimado/día', w: 34 }],
    res.presentes.map((c) => {
      const xs = res.porClase[c], h = xs[res.iHoy], p0 = xs.find((x) => x.completo) || xs[0];
      const val = (x) => (MAT[c].pila ? `${x.n} (${pct(x.area)})` : String(x.n));
      const cons = res.consumo[c];
      return [MAT[c].label, val(h), val(p0), cons ? (MAT[c].pila ? pct(cons) : num(cons)) : '-'];
    }));
  pdf.titulo('Evolución por día');
  pdf.tabla([{ t: 'Día', w: 30 }, ...res.presentes.map((c) => ({ t: MAT[c].corto, w: Math.floor(152 / res.presentes.length) }))],
    res.dias.map((k, i) => [`${fmtDow(k)} ${fmtKey(k)}${res.porClase[res.presentes[0]]?.[i]?.completo === false ? '*' : ''}`, ...res.presentes.map((c) => { const x = res.porClase[c][i]; return MAT[c].pila ? pct(x.area) : String(x.n); })]));
  pdf.nota('* Día con pocas fotos: no se usa para comparar.');
  if (res.avisos.length) {
    pdf.titulo('Cambios detectados');
    for (const av of res.avisos.slice(0, 12)) {
      pdf.texto(`${av.baja ? 'Bajó' : 'Subió'}: ${av.texto}`, { negrita: true });
      const z = zonaAviso(av);
      // eslint-disable-next-line no-await-in-loop
      const [ia, ib] = await Promise.all([av.a.foto ? imagenRecorte(av.a.foto, z, av.a.cajas.map((d) => ({ ...d, color: MAT[av.c].color })), 420) : null, av.b.foto ? imagenRecorte(av.b.foto, z, av.b.cajas.map((d) => ({ ...d, color: MAT[av.c].color })), 420) : null]);
      // eslint-disable-next-line no-await-in-loop
      await pdf.imagenes([{ img: ia, leyenda: `Antes · ${fmtKey(av.a.dia)}` }, { img: ib, leyenda: `Ahora · ${fmtKey(av.b.dia)}` }], { alto: 44 });
    }
  }
  pdf.guardar(nombreArchivo('acopio', name, rango));
}

export default function Acopio({ feedId, name }) {
  const narrow = useNarrow();
  const { rango, setRango, cargando, fotos, fuente } = useFotosV3(feedId, 14);
  const [sel, setSel] = useState(null);
  const [visor, setVisor] = useState(null);
  const [haciendoPdf, setHaciendoPdf] = useState(false);
  const res = useMemo(() => calcular(fotos), [fotos]);
  const clase = sel && res.presentes.includes(sel) ? sel : res.presentes[0];
  const ult = res.dias[res.iHoy];
  const esUltimo = res.iHoy === res.dias.length - 1;
  const serie = useMemo(() => (clase ? res.porClase[clase].filter((x) => x.completo).map((x) => ({
    label: `${fmtDow(x.dia)} ${x.dia.slice(8)}`, dia: x.dia, completo: x.completo, nFotos: x.nFotos,
    valor: MAT[clase].pila ? +(x.area * 100).toFixed(2) : x.n,
  })) : []), [res, clase]);
  const ver = (av) => setVisor([av.a, av.b].filter((x) => x.foto).map((x, i) => ({
    photo: x.foto, label: i === 0 ? 'Antes' : 'Ahora', boxes: x.cajas.map((d) => ({ ...d, color: MAT[av.c].color })),
    zoom: zonaAviso(av),
  })));
  const pdf = async () => { setHaciendoPdf(true); try { await generarPdf({ name, rango, res }); } finally { setHaciendoPdf(false); } };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <Encabezado icono="🧱" titulo="Acopio" obra={name} bajada="Lo que está acopiado en la obra según la cámara, y cómo va cambiando">
        <Rango rango={rango} setRango={setRango} />
      </Encabezado>
      {cargando ? <Card><Cargando /></Card> : fuente !== 'iaV3' ? <Card><SinAnalisis /></Card> : !res.dias.length ? <Card><Vacio>No hay fotos de jornada en el {textoRango(rango)}.</Vacio></Card> : (
        <>
          <Card title={<span>{esUltimo ? 'Hoy en obra' : 'Último día completo'} <span style={{ fontWeight: 400, color: T.muted, fontSize: 13 }}>· {cap(fmtDow(ult))} {fmtKey(ult)}</span></span>} right={<Btn onClick={pdf} disabled={haciendoPdf}>{haciendoPdf ? 'Armando PDF…' : '⬇ Descargar PDF'}</Btn>}>
            {!res.presentes.length ? <Vacio>No se ve material acopiado en la zona que toma la cámara.</Vacio> : (
              <div style={{ display: 'grid', gridTemplateColumns: `repeat(${narrow ? 2 : 4}, minmax(0, 1fr))`, gap: 10 }}>
                {res.presentes.map((c) => {
                  const xs = res.porClase[c], h = xs[res.iHoy], m = MAT[c];
                  const cons = res.consumo[c];
                  return (
                    <button key={c} type="button" onClick={() => setSel(c)} style={{ textAlign: 'left', padding: 0, background: 'none', border: 'none', cursor: 'pointer' }}>
                      <Kpi label={`${m.icon} ${m.label}`} color={clase === c ? m.color : undefined}
                        valor={m.pila ? (h.n ? `${h.n} · ${pct(h.area)}` : '0') : h.n}
                        sub={[m.pila ? 'pilas · superficie en la foto' : null, cons ? `consumo ≈ ${m.pila ? pct(cons) : num(cons)} por día` : null, !h.completo ? `día con pocas fotos (${h.nFotos})` : null].filter(Boolean).join(' · ') || null} />
                    </button>
                  );
                })}
              </div>
            )}
          </Card>
          {clase && (
            <Card title={<span>Evolución: {MAT[clase].label}</span>} right={(
              <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                {res.presentes.map((c) => <Chip key={c} color={MAT[c].color} on={clase === c} onClick={() => setSel(c)}>{MAT[c].icon} {cap(MAT[c].corto)}</Chip>)}
              </div>
            )}>
              <div style={{ width: '100%', height: 220 }}>
                <ResponsiveContainer>
                  <LineChart data={serie} margin={{ top: 6, right: 12, left: -18, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={T.line} />
                    <XAxis dataKey="label" tick={{ fontSize: 11, fill: T.muted }} />
                    <YAxis allowDecimals={MAT[clase].pila} tick={{ fontSize: 11, fill: T.muted }} unit={MAT[clase].pila ? '%' : ''} />
                    <Tooltip formatter={(v) => [MAT[clase].pila ? `${String(v).replace('.', ',')} % de la foto` : plural(v, MAT[clase].uno, MAT[clase].corto), 'A la vista']}
                      labelFormatter={(l, p) => (p?.[0]?.payload && !p[0].payload.completo ? `${l} (pocas fotos)` : l)} />
                    <Line type="linear" dataKey="valor" stroke={MAT[clase].color} strokeWidth={2.5} dot={{ r: 4 }} isAnimationActive={false} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
              <div style={{ fontSize: 12, color: T.muted, marginTop: 4 }}>
                {MAT[clase].pila ? 'Superficie que ocupan las pilas en la foto (crece si llega material, baja si se usa).' : 'Cantidad a la vista cada día.'} Los días con menos de {DIA_COMPLETO} fotos de jornada no se grafican ni se comparan.
              </div>
            </Card>
          )}
          <Card title={<span>Avisos ({res.avisos.length})</span>}>
            {!res.avisos.length ? <Vacio>Sin cambios importantes en el acopio en el {textoRango(rango)}.</Vacio> : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {res.avisos.map((av, i) => <Aviso key={i} av={av} onVer={ver} narrow={narrow} />)}
              </div>
            )}
          </Card>
          <Nota tono="aviso">
            <b>Conteo aproximado.</b> Es lo que se ve desde la cámara: no cuenta lo tapado (por lonas, otros materiales o un camión), lo guardado bajo techo
            ni lo que queda fuera de cuadro. Cuenta solo lo que está en el mismo lugar al menos dos días seguidos y en buena parte de las fotos del día
            (y, cuando está disponible, lo que se confirmó mirando un recorte de la foto). Un aviso de «ya no está» o «nuevo» se da solo si en el otro día,
            a una hora parecida (±1 h), hay al menos 3 fotos donde no se ve: así un cambio de luz o un hueco del detector no cuentan. Si la cámara se movió
            o cambió el zoom entre un día y otro, esa comparación no vale. Usalo como alerta para mirar, no como inventario contable.
          </Nota>
        </>
      )}
      {visor && <Visor fotos={visor} inicio={visor.length - 1} zoomZona={visor[0]?.zoom} onClose={() => setVisor(null)} />}
    </div>
  );
}
